'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { useState, useRef, useCallback, useEffect } from 'react';
import Head from 'next/head';
import { GemeindeAuswahl } from '@/components/GemeindeAuswahl';
import { PotenzialDashboard } from '@/components/organic/PotenzialDashboard';
import { PublicLandingPage } from '@/components/organic/PublicLandingPage';
import { EnergieDaten } from '@/components/EnergieDaten';
import { HeroSection } from '@/components/organic/HeroSection';
import { Leaf, Zap, Sun } from 'lucide-react';
import { analytics } from '@/utils/analytics';

export default function Home() {
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedGemeinde, setSelectedGemeinde] = useState<string | null>(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [gemeindeInfo, setGemeindeInfo] = useState<{ name: string } | null>(null);
  const [heroStats, setHeroStats] = useState<{ value: number; label: string; icon: React.ReactNode }[]>([]);
  const [dataSource, setDataSource] = useState<'kv' | 'local' | 'unknown'>('unknown');
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Check admin mode on component mount
  useEffect(() => {
    // Check if admin mode is enabled via URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const adminParam = urlParams.get('admin');
    
    // Admin mode is active if ?admin=true is in the URL
    setIsAdminMode(adminParam === 'true');
    setIsLoading(false);
  }, []);
  
  // Debug logging and analytics initialization
  useEffect(() => {
    console.log('[Page State]', { showDashboard, selectedGemeinde, dataSource });
    
    // Track page load and entry point
    analytics.trackPageView('/', 'Bürgerstrom - Was wäre wenn?');
    analytics.trackEntryPoint({
      entry_point: 'homepage',
      source: document.referrer ? 'referral' : 'direct',
    });
    analytics.trackDeviceInfo();
  }, [showDashboard, selectedGemeinde, dataSource]);

  // Check data source availability on component mount
  useEffect(() => {
    // Skip KV check on client side - always use local data for now
    // This prevents Redis warnings in the browser console
    setDataSource('local');
    console.log('[DataSource] Using local data (client-side)');
  }, []);

  const handleGemeindeSelect = (gemeinde: string) => {
    console.log('[Page] Gemeinde selected:', gemeinde);
    
    // Track municipality selection
    analytics.trackMunicipalitySelect(gemeinde, gemeinde, 'dropdown');
    
    setSelectedGemeinde(gemeinde);
    setShowDashboard(true);
    
    // Track navigation to dashboard
    analytics.trackNavigation('homepage', 'dashboard', 'click');
    
    // Scroll to top to show hero section
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const scrollToContent = useCallback(() => {
    analytics.trackButtonClick('scroll_to_content', 'hero_section');
    contentRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Callback to receive stats from PotenzialDashboard
  const handleStatsUpdate = useCallback((stats: { jahresPotenzial: number; anlagenGesamt: number; co2Ersparnis: number }) => {
    setHeroStats([
      { 
        value: stats.jahresPotenzial,
        label: 'Jährliches Potenzial (€)', 
        icon: <Zap className="h-5 w-5" /> 
      },
      { 
        value: stats.anlagenGesamt,
        label: 'Erneuerbare Anlagen', 
        icon: <Sun className="h-5 w-5" /> 
      },
      { 
        value: stats.co2Ersparnis,
        label: 'CO₂-Ersparnis (t/Jahr)', 
        icon: <Leaf className="h-5 w-5" /> 
      },
    ]);
  }, []);

  // Show loading state while checking test mode
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  // Show public landing page for non-admin users
  if (!isAdminMode) {
    return <PublicLandingPage />;
  }

  // Show the dashboard for admin users
  return (
    <>
      <Head>
        <title>Bürgerstrom - Was wäre wenn?</title>
        <meta name="description" content="Erfahren Sie, was Ihre Gemeinde mit einer Energiegemeinschaft erreichen könnte" />
        <link rel="icon" href="/favicon.ico" />
      </Head>


      <div className={`${!showDashboard ? 'min-h-screen bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-800' : ''}`}>
        {showDashboard && selectedGemeinde && heroStats.length > 0 && (
          <HeroSection
            title={`Energiezukunft in ${gemeindeInfo?.name || 'Ihrer Gemeinde'}`}
            subtitle="Entdecken Sie das ungenutzte Potenzial erneuerbarer Energien in Ihrer Gemeinde und werden Sie Teil der Energiewende."
            stats={heroStats}
            onScrollDown={scrollToContent}
          />
        )}
        <main ref={contentRef} className={`${showDashboard ? '' : 'container mx-auto px-4 py-8'}`}>
          <AnimatePresence mode="wait">
          {!showDashboard ? (
            <motion.div
              key="auswahl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="max-w-4xl mx-auto"
            >
              <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                  Was wäre wenn...
                </h1>
                <p className="text-xl text-gray-600 dark:text-gray-300">
                  Ihre Gemeinde Teil einer Energiegemeinschaft wäre?
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 mb-8">
                <GemeindeAuswahl onSelectGemeinde={handleGemeindeSelect} />
              </div>
              
              {selectedGemeinde && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8">
                  <EnergieDaten plz={selectedGemeinde} />
                </div>
              )}
              
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                  <div className="text-blue-500 text-3xl mb-3">💡</div>
                  <h3 className="font-semibold text-lg mb-2 dark:text-white">Energie teilen</h3>
                  <p className="text-gray-600 dark:text-gray-300">Lokale erneuerbare Energie optimal nutzen</p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                  <div className="text-green-500 text-3xl mb-3">💰</div>
                  <h3 className="font-semibold text-lg mb-2 dark:text-white">Kosten sparen</h3>
                  <p className="text-gray-600 dark:text-gray-300">Geld in Ihrer Gemeinde behalten</p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                  <div className="text-yellow-500 text-3xl mb-3">🌱</div>
                  <h3 className="font-semibold text-lg mb-2 dark:text-white">Klima schützen</h3>
                  <p className="text-gray-600 dark:text-gray-300">Beitrag zur Energiewende leisten</p>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="container mx-auto px-4 py-8"
            >
              {selectedGemeinde && (
                <PotenzialDashboard 
                  gemeinde={selectedGemeinde} 
                  onBack={() => {
                    analytics.trackNavigation('dashboard', 'homepage', 'back');
                    setShowDashboard(false);
                    setSelectedGemeinde(null);
                    setHeroStats([]);
                  }}
                  onStatsCalculated={handleStatsUpdate}
                  onGemeindeInfoLoaded={setGemeindeInfo}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

        <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-16">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center text-gray-500 dark:text-gray-400 text-sm">
              <p>© {new Date().getFullYear()} Bürgerstrom - Ein Projekt für nachhaltige Energiegemeinschaften in Tirol</p>
              <p className="mt-2 flex items-center justify-center gap-2">
                <span>Datenquelle: E-Control, Stand: Januar 2025</span>
                <span 
                  className={`inline-block w-2 h-2 rounded-full ${
                    dataSource === 'kv' ? 'bg-green-500' : 
                    dataSource === 'local' ? 'bg-orange-500' : 'bg-gray-400'
                  }`}
                  title={`Data source: ${
                    dataSource === 'kv' ? 'Live Database' : 
                    dataSource === 'local' ? 'Local Fallback' : 
                    'Checking...'
                  }`}
                />
                {process.env.NODE_ENV === 'development' && (
                  <span className="text-xs text-gray-400 ml-1">({dataSource})</span>
                )}
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
